import { useToast } from '@airship-platform/airship-patterns';
import { isItemGroupChild } from '@airship-platform/appointments-feature/utils';
import { BaseDialog, FlexBox } from '@airship-platform/jam-web/components';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import {
  AppointmentProposalOption,
  useAppointmentProposalOptions,
} from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposalOptionSOWs } from '../../../data-access/appointmentProposalOptionSows';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';

const generateUniqueOptionName = (
  baseName: string,
  options: AppointmentProposalOption[]
): string => {
  const existingNames = options.map((option) => option.name);
  let newName = `${baseName} copy`;
  let counter = 1;

  while (existingNames.includes(newName)) {
    newName = `${baseName} copy (${counter})`;
    counter += 1;
  }

  return newName;
};

interface DuplicateOptionModalProps {
  isOpen: boolean;
  optionId: string;
  onDuplicated: (option: AppointmentProposalOption) => unknown;
  onClose: () => unknown;
}

const DuplicateOptionModal: React.FC<DuplicateOptionModalProps> = ({
  isOpen,
  optionId,
  onDuplicated,
  onClose,
}) => {
  const { showToast } = useToast();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const { appendSelectedAppointmentProposalOptionsIds } =
    useAppointmentProposals();

  const { options, createAppointmentProposalOption } =
    useAppointmentProposalOptions();

  const {
    proposalProducts,
    getNextSortOrder: getNextProductSortOrder,
    createAppointmentProposalOptionProduct,
  } = useAppointmentProposalOptionProducts();

  const {
    proposalModifiers,
    getNextSortOrder: getNextModifierSortOrder,
    createAppointmentProposalOptionModifier,
  } = useAppointmentProposalOptionModifiers();

  const {
    proposalSows,
    getNextSortOrder: getNextSowSortOrder,
    createAppointmentProposalOptionSOW,
  } = useAppointmentProposalOptionSOWs();

  const selectedOption = options.find(
    (d) => d.appointmentProposalOptionId === optionId
  );
  if (!selectedOption) {
    return null;
  }

  const onOptionDuplicate = async () => {
    const {
      appointmentId,
      appointmentProposalId,
      appointmentProposalOptionId,
    } = selectedOption;

    const newOptionName = generateUniqueOptionName(
      selectedOption.name,
      options
    );

    setIsDuplicating(true);
    try {
      const newOption = await createAppointmentProposalOption(
        appointmentId,
        appointmentProposalId,
        {
          jobId: selectedOption.jobId,
          optionType: selectedOption.optionType,
          name: newOptionName,
          sortOrder: Math.max(0, ...options.map((o) => o.sortOrder + 1)),
          summary: selectedOption.summary,
          summaryTextPlain: selectedOption.summaryTextPlain,
          optionFinancingDisabled: selectedOption.optionFinancingDisabled,
          financingPlanId: selectedOption.financingPlanId,
          availableFinancingPlanIds: selectedOption.availableFinancingPlanIds,
          leasingPlan: selectedOption.leasingPlan,
        }
      );

      await appendSelectedAppointmentProposalOptionsIds([
        newOption.appointmentProposalOptionId,
      ]);

      const optionProducts = proposalProducts
        .filter(
          (product) =>
            product.appointmentProposalOptionId ===
              appointmentProposalOptionId &&
            product.groupingName !== 'LINKED_ITEM'
        )
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((product, index) => ({ product, index }));

      const duplicateProducts = async () => {
        const newRootIdByOldId = new Map<string, string>();

        // Pass 1: roots + normal lines, recording old→new ids for the children.
        await Promise.all(
          optionProducts
            .filter(({ product }) => !isItemGroupChild(product))
            .map(({ product, index }) => {
              const {
                appointmentProposalOptionProductId: oldProductId,
                ...create
              } = product;
              return createAppointmentProposalOptionProduct(
                appointmentId,
                appointmentProposalId,
                newOption.appointmentProposalOptionId,
                {
                  ...create,
                  sortOrder: getNextProductSortOrder(index),
                }
              ).then((created) => {
                newRootIdByOldId.set(
                  oldProductId,
                  created.appointmentProposalOptionProductId
                );
              });
            })
        );

        // Pass 2: children re-point at the NEW root; orphans degrade to normal lines.
        await Promise.all(
          optionProducts
            .filter(({ product }) => isItemGroupChild(product))
            .map(({ product, index }) => {
              const {
                appointmentProposalOptionProductId,
                itemGroupName,
                itemGroupRootId,
                ...create
              } = product;
              const newRootId = itemGroupRootId
                ? newRootIdByOldId.get(itemGroupRootId)
                : undefined;
              return createAppointmentProposalOptionProduct(
                appointmentId,
                appointmentProposalId,
                newOption.appointmentProposalOptionId,
                {
                  ...create,
                  ...(newRootId && { itemGroupRootId: newRootId }),
                  sortOrder: getNextProductSortOrder(index),
                }
              );
            })
        );
      };

      await Promise.all([
        duplicateProducts(),
        Promise.all(
          proposalModifiers
            .filter(
              (modifier) =>
                modifier.appointmentProposalOptionId ===
                appointmentProposalOptionId
            )
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ appointmentProposalOptionModifierId, ...modifier }, mIdx) =>
              createAppointmentProposalOptionModifier(
                appointmentId,
                appointmentProposalId,
                newOption.appointmentProposalOptionId,
                {
                  ...modifier,
                  sortOrder: getNextModifierSortOrder(mIdx),
                }
              )
            )
        ),
        Promise.all(
          proposalSows
            .filter(
              (sow) =>
                sow.appointmentProposalOptionId === appointmentProposalOptionId
            )
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ appointmentProposalOptionSOWId, ...sow }, mIdx) =>
              createAppointmentProposalOptionSOW(
                appointmentId,
                appointmentProposalId,
                newOption.appointmentProposalOptionId,
                {
                  ...sow,
                  sortOrder: getNextSowSortOrder(mIdx),
                }
              )
            )
        ),
      ]);

      onDuplicated(newOption);
      showToast(
        `${selectedOption.name} duplicated to ${newOption.name}`,
        'success'
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <BaseDialog
      fullWidth={false}
      maxWidth={'md'}
      open={isOpen}
      onClose={onClose}
      title={'Duplicate option'}
      footerAction={{
        left: {
          children: 'Cancel',
          onClick: onClose,
          color: 'softGold',
        },
        right: {
          children: 'Duplicate',
          onClick: () => onOptionDuplicate(),
          disabled: isDuplicating,
        },
      }}
    >
      <FlexBox
        alignItems={'center'}
        justifyContent={'center'}
        textAlign={'center'}
        p={4}
      >
        <Typography>
          Are you sure you want to duplicate option "
          <Typography component={'span'} sx={{ fontWeight: 800 }}>
            {selectedOption.name}
          </Typography>
          "?
        </Typography>
      </FlexBox>
    </BaseDialog>
  );
};

export default DuplicateOptionModal;
